import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { JourneyProvider } from './context/JourneyContext';
import { MockDateProvider } from './context/MockDateContext';


/* ── Eager-loaded (small, needed immediately) ── */
import LandingPage    from './pages/LandingPage';
import LoginPage      from './pages/LoginPage';
import SignupPage     from './pages/SignupPage';
import Layout         from './components/layout/Layout';

/* ── Lazy-loaded (split into separate chunks) ── */
const OnboardingPage     = lazy(() => import('./pages/OnboardingPage'));
const Dashboard          = lazy(() => import('./pages/Dashboard'));
const CycleStory         = lazy(() => import('./pages/CycleStory'));
const CycleHistory       = lazy(() => import('./pages/CycleHistory'));
const PeriodTracker      = lazy(() => import('./pages/PeriodTracker'));
const PregnancyStory     = lazy(() => import('./pages/PregnancyStory'));
const PregnancyTracker   = lazy(() => import('./pages/PregnancyTracker'));
const BabyCare           = lazy(() => import('./pages/BabyCare'));
const MemoryVault        = lazy(() => import('./pages/MemoryVault'));
const MedicalRecords     = lazy(() => import('./pages/MedicalRecords'));
const Community          = lazy(() => import('./pages/Community'));
const AIAssistant        = lazy(() => import('./pages/AIAssistant'));
const ProfileSettings    = lazy(() => import('./pages/ProfileSettings'));
const DeliveryPrep       = lazy(() => import('./pages/DeliveryPrep'));

/* ── Shared loading fallback ── */
const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center min-h-[60vh]">
    <div className="text-center">
      <Heart className="w-8 h-8 animate-heartbeat text-blush-400 mb-3 mx-auto" />
      <p className="text-pink-400 font-medium font-round text-sm">Loading…</p>
    </div>
  </div>
);

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center"
    style={{ background: 'linear-gradient(160deg,#fce4ec 0%,#f8bbd0 20%,#e8d5f0 50%,#fde8c8 80%,#fef3c7 100%)' }}>
    <div className="text-center">
      <Heart className="w-10 h-10 animate-heartbeat text-blush-400 mb-4 mx-auto" />
      <p className="text-pink-400 font-medium font-round text-sm">Loading TinySteps...</p>
    </div>
  </div>
);

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return children;
  return <Navigate to={user.onboardingComplete ? '/dashboard' : '/onboarding'} replace />;
};

const AuthRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboardingComplete) return <Navigate to="/dashboard" replace />;
  return children;
};

const AppRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboardingComplete) return <Navigate to="/onboarding" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
        <Route path="/onboarding" element={<AuthRoute><OnboardingPage /></AuthRoute>} />

        <Route path="/" element={<AppRoute><Layout /></AppRoute>}>
          <Route path="dashboard"         element={<Dashboard />} />
          <Route path="period"            element={<CycleStory />} />
          <Route path="period-log"        element={<Navigate to="/period" replace />} />
          <Route path="cycle-history"     element={<CycleHistory />} />
          <Route path="pregnancy"         element={<PregnancyTracker />} />
          <Route path="pregnancy-story"   element={<PregnancyStory />} />
          <Route path="pregnancy-tracker" element={<Navigate to="/pregnancy" replace />} />
          <Route path="baby"              element={<BabyCare />} />
          <Route path="memories"          element={<MemoryVault />} />
          <Route path="medical"           element={<MedicalRecords />} />
          <Route path="community"         element={<Community />} />
          <Route path="ai"                element={<AIAssistant />} />
          <Route path="delivery"          element={<DeliveryPrep />} />
          <Route path="profile"           element={<ProfileSettings />} />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
  );
}

function KeyedJourneyProvider({ children }) {
  const { user } = useAuth();
  return (
    <JourneyProvider key={user?._id || 'guest'} userId={user?._id || null}>
      {children}
    </JourneyProvider>
  );
}

function App() {
  return (

      <MockDateProvider>
        <ThemeProvider>
          <AuthProvider>
            <KeyedJourneyProvider>
              <Router>
                <AppRoutes />
              </Router>
            </KeyedJourneyProvider>
          </AuthProvider>
        </ThemeProvider>
      </MockDateProvider>

  );
}

export default App;
